import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import KitchenLoader from './KitchenLoader.jsx'

// AI invoice anomaly scanner.
//
// Phases:
//   generating — calls detect-receive-anomalies Edge Function
//   review     — show flagged anomalies with severity badges + reasoning
//   error      — retry path
//
// Informational only — no mutators. The manager reads the report
// and follows up with the supplier or fixes the underlying receive_log
// from the Receive screen if needed.
export default function AnomalyScanModal({
  open,
  onClose,
  receiveLogs,        // last 90 days
  windowDays = 90,
}) {
  const [phase, setPhase]    = useState('generating')
  const [error, setError]    = useState(null)
  const [result, setResult]  = useState(null)

  useEffect(() => {
    if (!open) return
    setPhase('generating')
    setError(null)
    setResult(null)
    let cancelled = false

    ;(async () => {
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const anonKey     = import.meta.env.VITE_SUPABASE_ANON_KEY
        const { data: sess } = await supabase.auth.getSession()
        const token = sess?.session?.access_token || anonKey

        const payload = {
          receive_logs: (receiveLogs || []).map((r) => ({
            id:                 r.id,
            item_id:            r.item_id,
            item_name:          r.item_name,
            quantity:           Number(r.quantity || 0),
            unit:               r.unit,
            cost_per_unit_at_log: Number(r.cost_per_unit_at_log || 0),
            supplier_name:      r.supplier_name,
            logged_at:          r.logged_at,
          })),
          window_days: windowDays,
        }

        const resp = await fetch(`${supabaseUrl}/functions/v1/detect-receive-anomalies`, {
          method: 'POST',
          headers: {
            'content-type':  'application/json',
            'authorization': `Bearer ${token}`,
            'apikey':        anonKey,
          },
          body: JSON.stringify(payload),
        })
        if (cancelled) return
        if (!resp.ok) {
          const errText = await resp.text()
          throw new Error(`HTTP ${resp.status}: ${errText.slice(0, 200) || resp.statusText}`)
        }
        const data = await resp.json()
        if (data.error) {
          setError(data.message || 'AI could not run the scan.')
          setPhase('error')
          return
        }
        setResult(data)
        setPhase('review')
      } catch (e) {
        if (cancelled) return
        // eslint-disable-next-line no-console
        console.error('[AnomalyScanModal] generate failed', e)
        setError('Could not scan — please try again in a moment.')
        setPhase('error')
      }
    })()

    return () => { cancelled = true }
  }, [open, receiveLogs, windowDays])

  if (!open) return null

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[95vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label="Invoice anomaly scan"
      >
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">✨ Invoice anomaly scan</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Close"
            >
              Close
            </button>
          </div>
          {error && (
            <div role="alert" className="mx-4 mb-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white shadow">
              {error}
            </div>
          )}
        </div>

        <div className="space-y-4 px-4 pb-4">
          {phase === 'generating' && (
            <KitchenLoader
              messages={[
                `Pulling the last ${windowDays} days of invoices\u2026`,
                'Comparing against your normal range\u2026',
                'Sniffing out anything off\u2026',
                'Plating the findings\u2026',
              ]}
            />
          )}

          {phase === 'error' && (
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn w-full rounded-lg border border-blue-600 bg-white px-4 py-3 text-base font-medium text-blue-700 hover:bg-blue-50"
            >
              Close and try again
            </button>
          )}

          {phase === 'review' && result && (
            <>
              {result.notes && (
                <div className={`rounded-xl border p-3 text-sm leading-relaxed ${
                  result.flagged_anomalies?.length > 0
                    ? 'border-amber-200 bg-amber-50/60 text-amber-900'
                    : 'border-emerald-200 bg-emerald-50/60 text-emerald-900'
                }`}>
                  <div className={`text-[11px] font-medium ${
                    result.flagged_anomalies?.length > 0
                      ? 'text-amber-900/70'
                      : 'text-emerald-900/70'
                  }`}>
                    Summary
                  </div>
                  <div className="mt-1">{result.notes}</div>
                </div>
              )}

              {result.data_caveats && result.data_caveats.length > 0 && (
                <details className="rounded-xl border border-black/10 bg-black/[0.02] p-3 text-xs text-black/65">
                  <summary className="cursor-pointer font-medium">
                    Data caveats ({result.data_caveats.length})
                  </summary>
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    {result.data_caveats.map((c, i) => <li key={i} className="leading-snug">{c}</li>)}
                  </ul>
                </details>
              )}

              {result.flagged_anomalies && result.flagged_anomalies.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-[11px] font-medium text-black/60">
                    Flagged ({result.flagged_anomalies.length})
                  </div>
                  {result.flagged_anomalies.map((a) => {
                    const tier = TONES[a.severity] || TONES.low
                    return (
                      <div
                        key={a.receive_log_id}
                        className={`rounded-xl border p-3 ${tier.border} ${tier.bg}`}
                      >
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-base font-medium">
                              {a.item_name}
                              <span className={`ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${tier.badge}`}>
                                {a.severity}
                              </span>
                              <span className="ml-2 inline-block rounded bg-black/10 px-1.5 py-0.5 text-[10px] font-medium text-black/65">
                                {(a.category || '').replace(/_/g, ' ')}
                              </span>
                            </div>
                          </div>
                        </div>

                        {a.current_value && (
                          <div className="mt-1 text-xs">
                            <span className="font-medium text-black/65">Flagged:</span>{' '}
                            <span className="text-black/85">{a.current_value}</span>
                          </div>
                        )}
                        {a.historical_context && (
                          <div className="text-xs">
                            <span className="font-medium text-black/65">History:</span>{' '}
                            <span className="text-black/85">{a.historical_context}</span>
                          </div>
                        )}

                        <div className="mt-2 text-sm leading-snug text-black/85">
                          {a.reasoning}
                        </div>

                        {a.suggested_action && (
                          <div className="mt-2 rounded-md bg-white/70 px-2 py-1.5 text-xs">
                            <span className="font-medium text-black/65">Suggested action:</span>{' '}
                            <span className="text-black/85">{a.suggested_action}</span>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/65">
                  No anomalies flagged. Your recent invoices look clean.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const TONES = {
  'high':   { badge: 'bg-red-600 text-white',           border: 'border-red-300',   bg: 'bg-red-50/40' },
  'medium': { badge: 'bg-amber-100 text-amber-900',     border: 'border-amber-200', bg: 'bg-amber-50/40' },
  'low':    { badge: 'bg-blue-100 text-blue-800',       border: 'border-blue-200',  bg: 'bg-blue-50/30' },
}
